/**
 * Chat Flow — visual flow definition (wire-compatible subset of
 * v6 flow-builder exports) + the session state persisted between user replies.
 *
 * Supported blocks: text/image bubbles, text/choice inputs, Condition,
 * Set variable, HTTP request ("Webhook") and Google Sheets. Anything else in
 * an imported JSON is reported by getUnsupportedFlowBlockTypes().
 */
import * as z from 'zod';

export enum FlowBlockType {
  TEXT_BUBBLE = 'text',
  IMAGE_BUBBLE = 'image',
  TEXT_INPUT = 'text input',
  CHOICE_INPUT = 'choice input',
  PICTURE_CHOICE_INPUT = 'picture choice input',
  CONDITION = 'Condition',
  SET_VARIABLE = 'Set variable',
  JUMP = 'Jump',
  WAIT = 'Wait',
  AB_TEST = 'AB test',
  HTTP_REQUEST = 'Webhook',
  GOOGLE_SHEETS = 'Google Sheets',
}

export enum FlowLogicalOperator {
  OR = 'OR',
  AND = 'AND',
}

export enum FlowComparisonOperator {
  EQUAL = 'Equal to',
  NOT_EQUAL = 'Not equal',
  CONTAINS = 'Contains',
  NOT_CONTAINS = 'Does not contain',
  GREATER = 'Greater than',
  GREATER_OR_EQUAL = 'Greater or equal to',
  LESS = 'Less than',
  LESS_OR_EQUAL = 'Less or equal to',
  IS_SET = 'Is set',
  IS_EMPTY = 'Is empty',
  STARTS_WITH = 'Starts with',
  ENDS_WITH = 'Ends with',
  MATCHES_REGEX = 'Matches regex',
  NOT_MATCH_REGEX = 'Does not match regex',
}

export enum GoogleSheetsAction {
  GET = 'Get data from sheet',
  INSERT_ROW = 'Insert a row',
  UPDATE_ROW = 'Update a row',
}

export const FLOW_INVALID_REPLY_MESSAGE = 'Invalid message. Please, try again.';

// ---------------------------------------------------------------------------
// Types (declared manually; zod schemas below are annotated with these so the
// emitted .d.ts stays small)
// ---------------------------------------------------------------------------

export interface FlowVariable {
  id: string;
  name: string;
  value?: string | string[] | null;
  isSessionVariable?: boolean;
}

export interface FlowComparison {
  id?: string;
  variableId?: string;
  comparisonOperator?: FlowComparisonOperator;
  value?: string;
}

export interface FlowCondition {
  logicalOperator?: FlowLogicalOperator;
  comparisons?: FlowComparison[];
}

interface FlowBlockBase {
  id: string;
  outgoingEdgeId?: string;
}

export interface TextBubbleBlock extends FlowBlockBase {
  type: FlowBlockType.TEXT_BUBBLE;
  content?: { richText?: unknown[]; plainText?: string };
}

export interface ImageBubbleBlock extends FlowBlockBase {
  type: FlowBlockType.IMAGE_BUBBLE;
  content?: { url?: string };
}

export interface TextInputBlock extends FlowBlockBase {
  type: FlowBlockType.TEXT_INPUT;
  options?: {
    variableId?: string;
    labels?: { placeholder?: string; button?: string };
  };
}

export interface ChoiceItem {
  id: string;
  outgoingEdgeId?: string;
  content?: string;
  value?: string;
  displayCondition?: { isEnabled?: boolean; condition?: FlowCondition };
}

export interface ChoiceInputBlock extends FlowBlockBase {
  type: FlowBlockType.CHOICE_INPUT;
  items?: ChoiceItem[];
  options?: {
    variableId?: string;
    isMultipleChoice?: boolean;
    buttonLabel?: string;
  };
}

export interface PictureChoiceItem {
  id: string;
  outgoingEdgeId?: string;
  pictureSrc?: string;
  title?: string;
  description?: string;
  value?: string;
  displayCondition?: { isEnabled?: boolean; condition?: FlowCondition };
}

export interface PictureChoiceInputBlock extends FlowBlockBase {
  type: FlowBlockType.PICTURE_CHOICE_INPUT;
  items?: PictureChoiceItem[];
  options?: {
    variableId?: string;
    isMultipleChoice?: boolean;
    buttonLabel?: string;
  };
}

export interface ConditionItem {
  id: string;
  outgoingEdgeId?: string;
  content?: FlowCondition;
}

export interface ConditionBlock extends FlowBlockBase {
  type: FlowBlockType.CONDITION;
  items?: ConditionItem[];
}

export interface SetVariableBlock extends FlowBlockBase {
  type: FlowBlockType.SET_VARIABLE;
  options?: {
    variableId?: string;
    /**
     * 'Custom' (default) and 'Empty' are executed; other flow variants
     * (Now, Append…) are logged and skipped.
     */
    type?: string;
    expressionToEvaluate?: string | null;
    isCode?: boolean;
  };
}

export interface JumpBlock extends FlowBlockBase {
  type: FlowBlockType.JUMP;
  /** groupId may also be a single {{var}} resolving to a group TITLE. */
  options?: { groupId?: string; blockId?: string };
}

export interface WaitBlock extends FlowBlockBase {
  type: FlowBlockType.WAIT;
  /** secondsToWaitFor may contain {{var}} references. */
  options?: { secondsToWaitFor?: string; shouldPause?: boolean };
}

export interface AbTestBlock extends FlowBlockBase {
  type: FlowBlockType.AB_TEST;
  items?: { id: string; path?: 'a' | 'b'; outgoingEdgeId?: string }[];
  options?: { aPercent?: number };
}

export interface FlowKeyValue {
  id?: string;
  key?: string;
  value?: string;
}

export interface ResponseVariableMapping {
  id?: string;
  variableId?: string;
  /** JS accessor evaluated against (statusCode, data), e.g. "data.items[0].name" */
  bodyPath?: string;
}

export interface HttpRequestBlock extends FlowBlockBase {
  type: FlowBlockType.HTTP_REQUEST;
  options?: {
    webhook?: {
      method?: string;
      url?: string;
      headers?: FlowKeyValue[];
      queryParams?: FlowKeyValue[];
      body?: string | null;
    };
    isCustomBody?: boolean;
    timeout?: number;
    responseVariableMapping?: ResponseVariableMapping[];
  };
}

export interface GoogleSheetsBlock extends FlowBlockBase {
  type: FlowBlockType.GOOGLE_SHEETS;
  options?: {
    credentialsId?: string;
    spreadsheetId?: string;
    sheetId?: string;
    action?: string;
    cellsToInsert?: FlowKeyValue[];
    cellsToUpsert?: FlowKeyValue[];
    referenceCell?: { column?: string; value?: string };
    filter?: {
      logicalOperator?: FlowLogicalOperator;
      comparisons?: {
        id?: string;
        column?: string;
        comparisonOperator?: FlowComparisonOperator;
        value?: string;
      }[];
    };
    cellsToExtract?: { id?: string; column?: string; variableId?: string }[];
  };
}

export type FlowBlock =
  | TextBubbleBlock
  | ImageBubbleBlock
  | TextInputBlock
  | ChoiceInputBlock
  | PictureChoiceInputBlock
  | ConditionBlock
  | SetVariableBlock
  | JumpBlock
  | WaitBlock
  | AbTestBlock
  | HttpRequestBlock
  | GoogleSheetsBlock;

export interface FlowGroup {
  id: string;
  title?: string;
  blocks: FlowBlock[];
}

export interface FlowEdge {
  id: string;
  from: { eventId?: string; blockId?: string; itemId?: string };
  to: { groupId: string; blockId?: string };
}

export interface FlowEvent {
  id: string;
  type: string;
  outgoingEdgeId?: string;
}

export interface FlowDefinition {
  version?: string;
  name?: string;
  events?: FlowEvent[] | null;
  groups: FlowGroup[];
  edges?: FlowEdge[];
  variables?: FlowVariable[];
}

/** Session state persisted (Redis) between user replies. */
export interface ChatFlowSessionState {
  version: 1;
  /** Input block the flow is paused at; undefined before start / after end. */
  currentBlockId?: string;
  /** Working copy of definition variables, holding current values. */
  variables: FlowVariable[];
  /** Answers collected so far ({ key: variable name or group title }). */
  answers: { key: string; value: string }[];
  /** bot_result row being filled for this session (updated every turn). */
  resultId?: number;
}

// ---------------------------------------------------------------------------
// Zod schemas (validation of imported/stored definitions)
// ---------------------------------------------------------------------------

const flowComparisonShape = {
  id: z.string().optional(),
  variableId: z.string().optional(),
  comparisonOperator: z.nativeEnum(FlowComparisonOperator).optional(),
  value: z.string().optional(),
};

const flowConditionShape = z
  .object({
    logicalOperator: z.nativeEnum(FlowLogicalOperator).optional(),
    comparisons: z
      .array(z.object(flowComparisonShape).passthrough())
      .optional(),
  })
  .passthrough();

const blockBase = {
  id: z.string(),
  outgoingEdgeId: z.string().optional(),
};

const keyValueShape = z
  .object({
    id: z.string().optional(),
    key: z.string().optional(),
    value: z.string().optional(),
  })
  .passthrough();

export const flowBlockSchema: z.ZodType<FlowBlock> = z.discriminatedUnion(
  'type',
  [
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.TEXT_BUBBLE),
        content: z
          .object({
            richText: z.array(z.unknown()).optional(),
            plainText: z.string().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.IMAGE_BUBBLE),
        content: z
          .object({ url: z.string().optional() })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.TEXT_INPUT),
        options: z
          .object({
            variableId: z.string().optional(),
            labels: z
              .object({
                placeholder: z.string().optional(),
                button: z.string().optional(),
              })
              .passthrough()
              .optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.CHOICE_INPUT),
        items: z
          .array(
            z
              .object({
                id: z.string(),
                outgoingEdgeId: z.string().optional(),
                content: z.string().optional(),
                value: z.string().optional(),
                displayCondition: z
                  .object({
                    isEnabled: z.boolean().optional(),
                    condition: flowConditionShape.optional(),
                  })
                  .passthrough()
                  .optional(),
              })
              .passthrough(),
          )
          .optional(),
        options: z
          .object({
            variableId: z.string().optional(),
            isMultipleChoice: z.boolean().optional(),
            buttonLabel: z.string().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.CONDITION),
        items: z
          .array(
            z
              .object({
                id: z.string(),
                outgoingEdgeId: z.string().optional(),
                content: flowConditionShape.optional(),
              })
              .passthrough(),
          )
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.SET_VARIABLE),
        options: z
          .object({
            variableId: z.string().optional(),
            type: z.string().optional(),
            expressionToEvaluate: z.string().nullish(),
            isCode: z.boolean().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.HTTP_REQUEST),
        options: z
          .object({
            webhook: z
              .object({
                method: z.string().optional(),
                url: z.string().optional(),
                headers: z.array(keyValueShape).optional(),
                queryParams: z.array(keyValueShape).optional(),
                body: z.string().nullish(),
              })
              .passthrough()
              .optional(),
            isCustomBody: z.boolean().optional(),
            timeout: z.number().optional(),
            responseVariableMapping: z
              .array(
                z
                  .object({
                    id: z.string().optional(),
                    variableId: z.string().optional(),
                    bodyPath: z.string().optional(),
                  })
                  .passthrough(),
              )
              .optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.PICTURE_CHOICE_INPUT),
        items: z
          .array(
            z
              .object({
                id: z.string(),
                outgoingEdgeId: z.string().optional(),
                pictureSrc: z.string().optional(),
                title: z.string().optional(),
                description: z.string().optional(),
                value: z.string().optional(),
                displayCondition: z
                  .object({
                    isEnabled: z.boolean().optional(),
                    condition: flowConditionShape.optional(),
                  })
                  .passthrough()
                  .optional(),
              })
              .passthrough(),
          )
          .optional(),
        options: z
          .object({
            variableId: z.string().optional(),
            isMultipleChoice: z.boolean().optional(),
            buttonLabel: z.string().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.JUMP),
        options: z
          .object({
            groupId: z.string().optional(),
            blockId: z.string().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.WAIT),
        options: z
          .object({
            secondsToWaitFor: z.string().optional(),
            shouldPause: z.boolean().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.AB_TEST),
        items: z
          .array(
            z
              .object({
                id: z.string(),
                path: z.enum(['a', 'b']).optional(),
                outgoingEdgeId: z.string().optional(),
              })
              .passthrough(),
          )
          .optional(),
        options: z
          .object({ aPercent: z.number().min(0).max(100).optional() })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    z
      .object({
        ...blockBase,
        type: z.literal(FlowBlockType.GOOGLE_SHEETS),
        options: z.record(z.unknown()).optional(),
      })
      .passthrough(),
  ],
) as unknown as z.ZodType<FlowBlock>;

export const flowDefinitionSchema: z.ZodType<FlowDefinition> = z
  .object({
    version: z.string().optional(),
    name: z.string().optional(),
    events: z
      .array(
        z
          .object({
            id: z.string(),
            type: z.string(),
            outgoingEdgeId: z.string().optional(),
          })
          .passthrough(),
      )
      .nullish(),
    groups: z.array(
      z
        .object({
          id: z.string(),
          title: z.string().optional(),
          blocks: z.array(flowBlockSchema),
        })
        .passthrough(),
    ),
    edges: z
      .array(
        z
          .object({
            id: z.string(),
            from: z
              .object({
                eventId: z.string().optional(),
                blockId: z.string().optional(),
                itemId: z.string().optional(),
              })
              .passthrough(),
            to: z
              .object({ groupId: z.string(), blockId: z.string().optional() })
              .passthrough(),
          })
          .passthrough(),
      )
      .optional(),
    variables: z
      .array(
        z
          .object({
            id: z.string(),
            name: z.string(),
            value: z.union([z.string(), z.array(z.string())]).nullish(),
            isSessionVariable: z.boolean().optional(),
          })
          .passthrough(),
      )
      .optional(),
  })
  .passthrough() as unknown as z.ZodType<FlowDefinition>;

/**
 * Flattens the Plate richText tree into WhatsApp-formatted text
 * (bold → *x*, italic → _x_, strikethrough → ~x~, code → `x`; paragraphs
 * joined with \n). Shared so the engine (what the bot sends) and the builder
 * preview/test panel format identically.
 */
interface RichTextLeaf {
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
  children?: RichTextLeaf[];
  type?: string;
  url?: string;
}

const applyRichTextMarks = (leaf: RichTextLeaf): string => {
  let text = leaf.text ?? '';
  if (!text.trim()) return text;
  if (leaf.bold) text = `*${text}*`;
  if (leaf.italic) text = `_${text}_`;
  if (leaf.strikethrough) text = `~${text}~`;
  if (leaf.code) text = `\`${text}\``;
  return text;
};

const serializeRichTextNode = (node: RichTextLeaf): string => {
  if (typeof node.text === 'string') return applyRichTextMarks(node);
  const children = (node.children ?? []).map(serializeRichTextNode).join('');
  if (node.type === 'a' && node.url)
    return children ? `${children} (${node.url})` : node.url;
  return children;
};

export const richTextToWhatsApp = (richText: unknown[]): string =>
  richText
    .map(node => serializeRichTextNode(node as RichTextLeaf))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');

/** The block types that pause the flow waiting for a client reply. */
export const isInputBlock = (
  block: FlowBlock,
): block is TextInputBlock | ChoiceInputBlock | PictureChoiceInputBlock =>
  block.type === FlowBlockType.TEXT_INPUT ||
  block.type === FlowBlockType.CHOICE_INPUT ||
  block.type === FlowBlockType.PICTURE_CHOICE_INPUT;

/** Variable ids bound to an input block (the answers persisted to results). */
export const getInputLinkedVariableIds = (
  definition: FlowDefinition,
): string[] => {
  const ids = new Set<string>();
  for (const group of definition.groups ?? []) {
    for (const block of group.blocks ?? []) {
      if (!isInputBlock(block)) continue;
      const variableId = block.options?.variableId;
      if (variableId) ids.add(variableId);
    }
  }
  return [...ids];
};

/**
 * Scans a raw (untrusted) flow export and lists block types we
 * don't support, so import endpoints can reject with a precise error.
 */
export const getUnsupportedFlowBlockTypes = (raw: unknown): string[] => {
  const supported = new Set<string>(Object.values(FlowBlockType));
  const groups = (raw as { groups?: { blocks?: { type?: unknown }[] }[] })
    ?.groups;
  if (!Array.isArray(groups)) return [];
  const unsupported = new Set<string>();
  for (const group of groups) {
    if (!Array.isArray(group?.blocks)) continue;
    for (const block of group.blocks) {
      if (typeof block?.type === 'string' && !supported.has(block.type))
        unsupported.add(block.type);
    }
  }
  return [...unsupported];
};
